import { eachMinuteOfInterval } from 'date-fns';

import { ALL_CATERING_FULFILLMENT_METHODS } from 'constants/fulfillmentOptions';
import {
  getDateTimeFromFulfillmentDateAndTime,
  getFulfillmentTimeDisplay,
  getFulfillmentTimeKey
} from 'catering/helpers';

import { sortByDistance } from 'helpers/locations/sortByDistance';
import { filterByIsClosed } from 'helpers/locations/filterByIsClosed';

export function selectCateringLocationById(locations, locationId) {
  return locations.find(location => location.id === locationId);
}

export function selectCateringLocationBySlug(locations, locationSlug) {
  return locations.find(({ location }) => location.slug === locationSlug);
}

export function selectAreAllLocationsClosed(locations) {
  return locations.every(l => l.is_closed);
}

export function selectCateringLocationAvailableFulfillmentOptions(location) {
  return ALL_CATERING_FULFILLMENT_METHODS.filter(
    (method, i) =>
      ![location?.is_closed_for_delivery, location?.is_closed_for_pickup][i]
  );
}

/***
 * This method will take the paginated response from fulfillment-hours endpoint
 * which for each page it will be the in the following shape:
 *
 * { '2021-07-25': [{start: "18:00:00", end: "18:30:00", fulfillment_menu_id: 39, time_windows_length_in_minutes: 15}] }
 *
 * and parse into an object of fulfillment date times in the following shape:
 *
 * { '2021-07-25: [
 *   {value: "18:00:00", label: "06:00pm - 06:15pm", fulfillmentMenuId: 39, timeWindowsIntervalInMinutes: 15},
 *   {value: "18:00:00", label: "06:15pm - 06:30pm", fulfillmentMenuId: 39, timeWindowsIntervalInMinutes: 15}
 * ]}
 *
 * @param fulfillmentHours
 * @returns {{}}
 */
export function selectCateringLocationFulfillmentHours(fulfillmentHours) {
  return Object.entries(Object.assign(...fulfillmentHours.pages)).reduce(
    (allHours, [date, hours]) => {
      allHours[date] = hours
        .map(hour => {
          const { start, end, menu_id, time_windows_length_in_minutes } = hour;

          const startDate = getDateTimeFromFulfillmentDateAndTime(date, start);
          const endDate = getDateTimeFromFulfillmentDateAndTime(date, end);

          // if fulfillment times are discrete, we break time options in 15 minute intervals
          const timeWindowsIntervalInMinutes =
            time_windows_length_in_minutes || 15;

          // generate all intervals between start and end dates
          const timeIntervals = eachMinuteOfInterval(
            {
              start: startDate,
              end: endDate
            },
            {
              step: timeWindowsIntervalInMinutes
            }
          );

          const timeOptions = [];

          // if fulfillment times are in ranges, we format then in the following format:
          // {value: "18:00:00", label: "06:15pm - 06:30pm", timeWindowsIntervalInMinutes: 15, fulfillmentMenuId: .., }
          if (time_windows_length_in_minutes) {
            timeIntervals.forEach((interval, i) => {
              if (timeIntervals[i + 1]) {
                timeOptions.push({
                  value: getFulfillmentTimeKey(interval),
                  label: `${getFulfillmentTimeDisplay(
                    interval
                  )} - ${getFulfillmentTimeDisplay(timeIntervals[i + 1])}`,
                  fulfillmentMenuId: menu_id,
                  timeWindowsIntervalInMinutes: time_windows_length_in_minutes
                });
              }
            });
            // otherwise we use discrete times
            // {value: "18:00:00", label: "06:00pm", timeWindowsIntervalInMinutes: 0, fulfillmentMenuId: .., }
          } else {
            timeIntervals.forEach((interval, i) => {
              if (timeIntervals[i + 1]) {
                timeOptions.push({
                  value: getFulfillmentTimeKey(interval),
                  label: getFulfillmentTimeDisplay(interval),
                  fulfillmentMenuId: menu_id,
                  timeWindowsIntervalInMinutes: time_windows_length_in_minutes
                });
              }
            });
          }

          return timeOptions;
        })
        .flat();

      return allHours;
    },
    {}
  );
}

export function selectPickupLocations(locations = []) {
  return locations
    .filter(
      location =>
        !location.is_closed &&
        Object.prototype.hasOwnProperty.call(
          location,
          'does_deliver_to_address'
        ) &&
        !location.does_deliver_to_address &&
        !location.is_closed_for_pickup
    )
    .sort(sortByDistance);
}

export function selectDeliverToAddressLocations(locations) {
  return locations
    .filter(
      location =>
        !location.is_closed &&
        Object.prototype.hasOwnProperty.call(
          location,
          'does_deliver_to_address'
        ) &&
        location.does_deliver_to_address &&
        !location.is_closed_for_delivery
    )
    .sort(sortByDistance);
}

export function selectClosedLocations(locations) {
  return filterByIsClosed(locations);
}
